import { useEffect } from "react";
import "./Borrow.css";
import usePageMeta from "../../hooks/usePageMeta";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to Borrow Against Bitcoin with Money Protocol",
  description:
    "A step-by-step guide for Bitcoin holders to deposit BTC as collateral on Rootstock and mint BPD, a USD-pegged stablecoin, at 0% interest with no KYC and no custodian.",
  image: "https://www.moneyprotocol.co/og/borrow-against-bitcoin.png",
  totalTime: "PT15M",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "USD",
    value: "0",
  },
  supply: [
    {
      "@type": "HowToSupply",
      name: "RBTC (Bitcoin on Rootstock) — at least enough to maintain the 110% minimum collateral ratio for the amount you want to borrow",
    },
    {
      "@type": "HowToSupply",
      name: "A small amount of RBTC for transaction gas",
    },
  ],
  tool: [
    {
      "@type": "HowToTool",
      name: "A self-custodial Web3 wallet (e.g. MetaMask) configured for the Rootstock network",
    },
  ],
  step: [
    {
      "@type": "HowToStep",
      name: "Connect your wallet",
      text: "Open www.bitcoinstable.money and connect a self-custodial Web3 wallet such as MetaMask. Make sure the wallet is configured for the Rootstock network (chain ID 30 for mainnet). Money Protocol never custodies your keys — every action is signed by your wallet.",
      url: "https://www.moneyprotocol.co/borrow#step-1-connect-wallet",
      image: "https://www.moneyprotocol.co/og/borrow-against-bitcoin.png",
    },
    {
      "@type": "HowToStep",
      name: "Get RBTC",
      text: "Money Protocol uses RBTC (Bitcoin on Rootstock) as collateral. You can bridge BTC to RBTC using the PowPeg bridge, or buy RBTC directly on an exchange that supports the Rootstock network. Send the RBTC to your connected wallet address.",
      url: "https://www.moneyprotocol.co/borrow#step-2-get-rbtc",
    },
    {
      "@type": "HowToStep",
      name: "Open a vault and deposit RBTC as collateral",
      text: "In the Money Protocol app, open a new vault and deposit RBTC as collateral. Choose how much BPD you want to mint against it. You must maintain a minimum 110% collateral ratio at all times — so $1,100 of RBTC backs $1,000 of BPD. There is no ongoing interest, only a one-time borrowing fee built into the BPD you receive.",
      url: "https://www.moneyprotocol.co/borrow#step-3-open-vault",
    },
    {
      "@type": "HowToStep",
      name: "Withdraw your BPD",
      text: "Once your vault is open, the minted BPD is in your wallet. BPD is a USD-pegged stablecoin you can spend, swap on Intrinsic.finance or other Rootstock DEXs, hold as a dollar-denominated balance, or deposit into the Money Protocol stability pool to earn liquidation rewards.",
      url: "https://www.moneyprotocol.co/borrow#step-4-withdraw-bpd",
    },
    {
      "@type": "HowToStep",
      name: "Repay and reclaim your Bitcoin",
      text: "Whenever you choose — there is no fixed term — return the BPD you borrowed to close your vault. Your full RBTC collateral is released back to your wallet. You can also partially repay to reduce your debt and improve your collateral ratio.",
      url: "https://www.moneyprotocol.co/borrow#step-5-repay",
    },
  ],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: "https://www.moneyprotocol.co/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Borrow",
      item: "https://www.moneyprotocol.co/borrow",
    },
  ],
};

export default function Borrow() {
  usePageMeta(
    "How to Borrow Against Bitcoin at 0% Interest | Money Protocol",
    "Step-by-step guide to borrowing BPD against your Bitcoin using Money Protocol on Rootstock. 0% interest, self-custodial, no KYC. Five steps from wallet connect to receiving BPD.",
    "https://www.moneyprotocol.co/borrow",
  );

  useEffect(() => {
    const howToScript = document.createElement("script");
    howToScript.type = "application/ld+json";
    howToScript.text = JSON.stringify(howToSchema);
    document.head.appendChild(howToScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(howToScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  return (
    <div className="borrow-page">
      <div className="borrow-container">
        <nav className="crumbs">
          <a href="/">Home</a> &nbsp;/&nbsp; <span>Borrow</span>
        </nav>
      </div>

      <main>
        <div className="borrow-container">
          <h1>How to Borrow Against Bitcoin at 0% Interest</h1>
          <p className="lede">
            Five steps to deposit Bitcoin as collateral and mint BPD &mdash; a
            USD-pegged stablecoin &mdash; without giving up custody, paying
            interest, or signing up anywhere.
          </p>

          <div className="quickfacts">
            <div className="quickfact">
              <div className="label">Interest rate</div>
              <div className="value">0%</div>
            </div>
            <div className="quickfact">
              <div className="label">Minimum collateral ratio</div>
              <div className="value">110%</div>
            </div>
            <div className="quickfact">
              <div className="label">Custody</div>
              <div className="value">Self &mdash; always</div>
            </div>
            <div className="quickfact">
              <div className="label">KYC</div>
              <div className="value">None</div>
            </div>
            <div className="quickfact">
              <div className="label">Time to first borrow</div>
              <div className="value">~15 minutes</div>
            </div>
            <div className="quickfact">
              <div className="label">Network</div>
              <div className="value">Rootstock</div>
            </div>
          </div>

          <p>
            Money Protocol lets you use your Bitcoin without selling it. You
            deposit RBTC (Bitcoin on the Rootstock sidechain) as collateral, and
            the protocol mints <strong>BPD</strong> &mdash; a stablecoin pegged
            to the U.S. dollar &mdash; against it. There is no interest, no
            fixed term, no custodian, and no admin key. You repay the BPD
            whenever you want and reclaim your Bitcoin. The protocol is modeled
            on{" "}
            <a
              className="inline"
              href="https://www.liquity.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Liquity
            </a>
            , adapted for Bitcoin.
          </p>

          <h2>The five steps</h2>
          <ol className="steps">
            <li className="step" id="step-1-connect-wallet">
              <h3>Connect your wallet</h3>
              <p>
                Open{" "}
                <a
                  className="inline"
                  href="https://www.bitcoinstable.money/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.bitcoinstable.money
                </a>{" "}
                and connect a self-custodial Web3 wallet such as MetaMask. Make
                sure the wallet is configured for the Rootstock network (chain
                ID 30 for mainnet).
              </p>
              <p>
                Money Protocol never custodies your keys &mdash; every action is
                signed by your wallet.
              </p>
            </li>

            <li className="step" id="step-2-get-rbtc">
              <h3>Get RBTC</h3>
              <p>
                Money Protocol uses <strong>RBTC</strong> (Bitcoin on Rootstock)
                as collateral. You have two paths to get it:
              </p>
              <p>
                <strong>Bridge:</strong> Use the{" "}
                <a
                  className="inline"
                  href="https://powpeg.rootstock.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PowPeg bridge
                </a>{" "}
                to convert native BTC into RBTC. <strong>Buy:</strong> Acquire
                RBTC directly on an exchange that supports the Rootstock
                network. Send the RBTC to your connected wallet address.
              </p>
            </li>

            <li className="step" id="step-3-open-vault">
              <h3>Open a vault and deposit RBTC as collateral</h3>
              <p>
                In the Money Protocol app, open a new vault and deposit RBTC as
                collateral. Choose how much BPD you want to mint against it.
              </p>
              <p>
                You must maintain a{" "}
                <strong>minimum 110% collateral ratio</strong> at all times
                &mdash; so $1,100 of RBTC backs $1,000 of BPD. There is no
                ongoing interest; only a one-time borrowing fee built into the
                BPD you receive. Vaults below 110% are liquidated by the
                stability pool.
              </p>
            </li>

            <li className="step" id="step-4-withdraw-bpd">
              <h3>Withdraw your BPD</h3>
              <p>
                Once your vault is open, the minted BPD is in your wallet. From
                there, you can:
              </p>
              <p>
                Spend it, swap it on{" "}
                <a
                  className="inline"
                  href="https://www.intrinsic.finance/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Intrinsic.finance
                </a>{" "}
                or other Rootstock DEXs, hold it as a USD-denominated balance,
                or deposit it into the Money Protocol{" "}
                <strong>stability pool</strong> to earn liquidation rewards
                (RBTC) and MP token incentives.
              </p>
            </li>

            <li className="step" id="step-5-repay">
              <h3>Repay and reclaim your Bitcoin</h3>
              <p>
                Whenever you choose &mdash; there is no fixed term &mdash;
                return the BPD you borrowed to close your vault. Your full RBTC
                collateral is released back to your wallet.
              </p>
              <p>
                You can also partially repay to reduce your debt and improve
                your collateral ratio without closing the vault.
              </p>
            </li>
          </ol>

          <div className="cta">
            <h2>Ready to borrow?</h2>
            <p>
              The app is live on Rootstock mainnet. Connect your wallet and open
              your first vault.
            </p>
            <a
              className="btn btn-primary"
              href="https://www.bitcoinstable.money/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Launch Now
            </a>
            <a
              className="btn btn-secondary"
              href="https://docs.moneyprotocol.co/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read the docs
            </a>
          </div>

          <h2>Frequently asked</h2>
          <div className="faq-block">
            <p>
              <strong>Is there a credit check or KYC?</strong>
              <br />
              No. Money Protocol is a smart contract on Rootstock &mdash; the
              only requirement is RBTC collateral.
            </p>
            <p>
              <strong>Can the team freeze my vault?</strong>
              <br />
              No. There is no admin key, no upgrade pause, and no team-controlled
              multisig that can touch user vaults.
            </p>
            <p>
              <strong>What happens if Bitcoin&rsquo;s price falls?</strong>
              <br />
              If your collateral ratio drops below 110%, your vault is liquidated
              &mdash; the stability pool absorbs the BPD debt and receives your
              RBTC collateral. You keep the BPD you borrowed.
            </p>
            <p>
              <strong>What is the borrowing fee?</strong>
              <br />A small one-time fee is added to your debt at borrow time.
              There is no recurring interest after that.
            </p>
          </div>
          <p className="meta">
            More questions on the{" "}
            <a className="inline" href="/faq">
              full FAQ
            </a>{" "}
            and the{" "}
            <a
              className="inline"
              href="https://docs.moneyprotocol.co/"
              target="_blank"
              rel="noopener noreferrer"
            >
              docs
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
